import * as types from 'src/state/ducks/order/types';
import { OrderEntity } from 'src/firebase/db/Orders';
import { APISuccessfulResponse, APIFailedResponse } from 'src/firebase/db/DatabaseAdapter';
import { ValidationResponse } from 'src/firebase/db/ValidationManager';

export type Actions =
  | GetOrders
  | SuccessGetOrders
  | FailGetOrders
  | GetDayOrders
  | SuccessGetDayOrders
  | FailGetDayOrders
  | GetUsersOrders
  | SuccessGetUsersOrders
  | FailGetUsersOrders
  | EditOrder
  | ValidateOrder
  | SuccessValidateOrder
  | FailValidateOrder
  | CreateOrder
  | SuccessCreateOrder
  | FailCreateOrder
  | UpdateOrderIsConfirmed
  | SuccessUpdateOrderIsConfirmed
  | FailUpdateOrderIsConfirmed
  | UpdateOrderIsCompleted
  | SuccessUpdateOrderIsCompleted
  | FailUpdateOrderIsCompleted
  | InitOrder;

/**
 * Get
 */
interface GetOrders {
  type: typeof types.GET_ORDERS;
  payload: {
    shopId: string;
  };
}
export const getOrders = (payload: GetOrders['payload']) => ({
  type: types.GET_ORDERS,
  payload,
});

interface SuccessGetOrders {
  type: typeof types.SUCCESS_GET_ORDERS;
  payload: APISuccessfulResponse<OrderEntity[]>;
}
export const successGetOrders = (payload: SuccessGetOrders['payload']) => ({
  type: types.SUCCESS_GET_ORDERS,
  payload,
});

interface FailGetOrders {
  type: typeof types.FAIL_GET_ORDERS;
  payload: ValidationResponse | APIFailedResponse;
}
export const failGetOrders = (payload: FailGetOrders['payload']) => ({
  type: types.FAIL_GET_ORDERS,
  payload,
});

/**
 * ReEdit
 */
interface EditOrder {
  type: typeof types.EDIT_ORDER;
  payload: OrderEntity;
}
export const editOrder = (payload: ValidateOrder['payload']) => ({
  type: types.EDIT_ORDER,
  payload,
});

/**
 * Validate
 */
interface ValidateOrder {
  type: typeof types.VALIDATE_ORDER;
  payload: OrderEntity;
}
export const validateOrder = (payload: ValidateOrder['payload']) => ({
  type: types.VALIDATE_ORDER,
  payload,
});

interface SuccessValidateOrder {
  type: typeof types.SUCCESS_VALIDATE_ORDER;
  payload: APISuccessfulResponse<OrderEntity>;
}
export const successValidateOrder = (payload: SuccessValidateOrder['payload']) => ({
  type: types.SUCCESS_VALIDATE_ORDER,
  payload,
});

interface FailValidateOrder {
  type: typeof types.FAIL_VALIDATE_ORDER;
  payload: ValidationResponse | APIFailedResponse;
}
export const failValidateOrder = (payload: FailValidateOrder['payload']) => ({
  type: types.FAIL_VALIDATE_ORDER,
  payload,
});

/**
 * Create
 */
interface CreateOrder {
  type: typeof types.CREATE_ORDER;
  payload: string;
}
export const createOrder = (payload: CreateOrder['payload']) => ({
  type: types.CREATE_ORDER,
  payload,
});

interface SuccessCreateOrder {
  type: typeof types.SUCCESS_CREATE_ORDER;
  payload: APISuccessfulResponse<OrderEntity>;
}
export const successCreateOrder = (payload: SuccessCreateOrder['payload']) => ({
  type: types.SUCCESS_CREATE_ORDER,
  payload,
});

interface FailCreateOrder {
  type: typeof types.FAIL_CREATE_ORDER;
  payload: ValidationResponse | APIFailedResponse;
}
export const failCreateOrder = (payload: FailCreateOrder['payload']) => ({
  type: types.FAIL_CREATE_ORDER,
  payload,
});

/**
 * Get: by date range
 */
interface GetDayOrders {
  type: typeof types.GET_DAY_ORDERS;
  payload: {
    shopId: string;
    startDate: Date;
    endDate: Date;
    target: 'created_at' | 'last_pickup_time';
    filter?: 'normal' | 'reservation';
    noUsers?: boolean; // ユーザー情報の照会を行わない
    noDetails?: boolean; // 明細を取得しない
  };
}
export const getDayOrders = (payload: GetDayOrders['payload']) => ({
  type: types.GET_DAY_ORDERS,
  payload,
});

interface SuccessGetDayOrders {
  type: typeof types.SUCCESS_GET_DAY_ORDERS;
  payload: APISuccessfulResponse<OrderEntity[]>;
}
export const successGetDayOrders = (payload: SuccessGetDayOrders['payload']) => ({
  type: types.SUCCESS_GET_DAY_ORDERS,
  payload,
});

interface FailGetDayOrders {
  type: typeof types.FAIL_GET_DAY_ORDERS;
  payload: ValidationResponse | APIFailedResponse;
}
export const failGetDayOrders = (payload: FailGetDayOrders['payload']) => ({
  type: types.FAIL_GET_DAY_ORDERS,
  payload,
});

/**
 * Get: by user_id[]
 */
interface GetUsersOrders {
  type: typeof types.GET_USERS_ORDERS;
  payload: string[];
}
export const getUsersOrders = (payload: GetUsersOrders['payload']) => ({
  type: types.GET_USERS_ORDERS,
  payload,
});

interface SuccessGetUsersOrders {
  type: typeof types.SUCCESS_GET_USERS_ORDERS;
  payload: APISuccessfulResponse<OrderEntity[]>;
}
export const successGetUsersOrders = (payload: SuccessGetUsersOrders['payload']) => ({
  type: types.SUCCESS_GET_USERS_ORDERS,
  payload,
});

interface FailGetUsersOrders {
  type: typeof types.FAIL_GET_USERS_ORDERS;
  payload: ValidationResponse | APIFailedResponse;
}
export const failGetUsersOrders = (payload: FailGetUsersOrders['payload']) => ({
  type: types.FAIL_GET_USERS_ORDERS,
  payload,
});

/**
 * Update: is_confirmed
 */
interface UpdateOrderIsConfirmed {
  type: typeof types.UPDATE_ORDER_IS_CONFIRMED;
  payload: {
    id: string;
    isConfirmed: boolean;
  };
}
export const updateOrderIsConfirmed = (payload: UpdateOrderIsConfirmed['payload']) => ({
  type: types.UPDATE_ORDER_IS_CONFIRMED,
  payload,
});

interface SuccessUpdateOrderIsConfirmed {
  type: typeof types.SUCCESS_UPDATE_ORDER_IS_CONFIRMED;
  payload: APISuccessfulResponse<OrderEntity>;
}
export const successUpdateOrderIsConfirmed = (
  payload: SuccessUpdateOrderIsConfirmed['payload'],
) => ({
  type: types.SUCCESS_UPDATE_ORDER_IS_CONFIRMED,
  payload,
});

interface FailUpdateOrderIsConfirmed {
  type: typeof types.FAIL_UPDATE_ORDER_IS_CONFIRMED;
  payload: ValidationResponse | APIFailedResponse;
}
export const failUpdateOrderIsConfirmed = (payload: FailUpdateOrderIsConfirmed['payload']) => ({
  type: types.FAIL_UPDATE_ORDER_IS_CONFIRMED,
  payload,
});

/**
 * Update: is_completed
 */
interface UpdateOrderIsCompleted {
  type: typeof types.UPDATE_ORDER_IS_COMPLETED;
  payload: {
    id: string;
    isCompleted: boolean;
  };
}
export const updateOrderIsCompleted = (payload: UpdateOrderIsCompleted['payload']) => ({
  type: types.UPDATE_ORDER_IS_COMPLETED,
  payload,
});

interface SuccessUpdateOrderIsCompleted {
  type: typeof types.SUCCESS_UPDATE_ORDER_IS_COMPLETED;
  payload: APISuccessfulResponse<OrderEntity>;
}
export const successUpdateOrderIsCompleted = (
  payload: SuccessUpdateOrderIsCompleted['payload'],
) => ({
  type: types.SUCCESS_UPDATE_ORDER_IS_COMPLETED,
  payload,
});

interface FailUpdateOrderIsCompleted {
  type: typeof types.FAIL_UPDATE_ORDER_IS_COMPLETED;
  payload: ValidationResponse | APIFailedResponse;
}
export const failUpdateOrderIsCompleted = (payload: FailUpdateOrderIsCompleted['payload']) => ({
  type: types.FAIL_UPDATE_ORDER_IS_COMPLETED,
  payload,
});

/**
 * Init state
 */
interface InitOrder {
  type: typeof types.INIT_ORDER;
}
export const initOrder = () => ({
  type: types.INIT_ORDER,
});
